import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { api, unwrap } from "../api/client";
import { useSession } from "./session";

const KEY = ["me", "security"] as const;
const when = (t: string) => new Date(t).toLocaleString();

/** The caller's sign-ins and remembered browsers, which they can end. */
export function AccountPage() {
  const qc = useQueryClient();
  const desktop = useSession().data?.desktop ?? false;
  const sec = useQuery({
    queryKey: KEY,
    queryFn: () => unwrap(api.GET("/api/me/security")),
  });
  const done = () => void qc.invalidateQueries({ queryKey: KEY });
  const end = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/me/sessions/{id}/end", { params: { path: { id } } }),
      ),
    onSuccess: done,
  });
  const forget = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST("/api/me/devices/{id}/forget", { params: { path: { id } } }),
      ),
    onSuccess: done,
  });
  const s = sec.data;
  return (
    <Stack spacing={3} component="section" aria-label="My account">
      <Typography variant="h5" component="h2">
        My account
      </Typography>
      {sec.isError && <Alert severity="error">{sec.error.message}</Alert>}
      {(end.isError || forget.isError) && (
        <Alert severity="error">{(end.error ?? forget.error)?.message}</Alert>
      )}
      {s && !desktop && (
        <Typography>
          Two-factor sign-in is {s.mfa_enrolled ? "set up" : "not set up"}.{" "}
          {s.mfa_enrolled &&
            `${s.recovery_codes_left} of your recovery codes are left; if you run out or lose them, ask an administrator to reset your two-factor sign-in.`}
        </Typography>
      )}
      <Paper variant="outlined">
        <Typography variant="h6" component="h3" sx={{ p: 2, pb: 0 }}>
          Where you are signed in
        </Typography>
        <Table size="small" aria-label="Sessions">
          <TableHead>
            <TableRow>
              <TableCell>Signed in</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell>From</TableCell>
              <TableCell>Browser</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {s?.sessions.map((x) => (
              <TableRow key={x.id}>
                <TableCell>{when(x.created_at)}</TableCell>
                <TableCell>{when(x.last_seen)}</TableCell>
                <TableCell>{x.ip ?? "This computer"}</TableCell>
                <TableCell sx={{ maxWidth: 320, overflowWrap: "anywhere" }}>
                  {x.user_agent}
                </TableCell>
                <TableCell>
                  {x.current ? (
                    <Chip size="small" label="This one" />
                  ) : (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => end.mutate(x.id)}
                    >
                      Sign out
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      {!desktop && (
        <Paper variant="outlined">
          <Typography variant="h6" component="h3" sx={{ p: 2, pb: 0 }}>
            Browsers that skip the code
          </Typography>
          <Table size="small" aria-label="Remembered browsers">
            <TableHead>
              <TableRow>
                <TableCell>Browser</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell>Until</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {s?.devices.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>None.</TableCell>
                </TableRow>
              )}
              {s?.devices.map((d) => (
                <TableRow key={d.id}>
                  <TableCell sx={{ maxWidth: 320, overflowWrap: "anywhere" }}>
                    {d.label}
                  </TableCell>
                  <TableCell>{when(d.last_used_at)}</TableCell>
                  <TableCell>{when(d.expires_at)}</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      color="error"
                      onClick={() => forget.mutate(d.id)}
                    >
                      Forget
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
